import type { PremiumKind } from '@words-happen/board-rules-engine-client';
import { findRules } from '@words-happen/board-rules-engine-client';
import type { TileData } from '@words-happen/game-components';
import { pointsLabel, refusalMessage } from './board-game-labels';
import type { BoardGameView } from './board-game-types';

/** What the tiles laid this turn would do if sent now. Only a guide: the server's answer is what counts. */
export type BoardGamePreview =
  | { valid: true; words: string[]; points: number; message: string }
  | { valid: false; message: string };

const PREMIUM_KINDS: ReadonlySet<string> = new Set<PremiumKind>(['2W', '3W']);

/**
 * The move the tiles laid this turn make, checked and scored by the game's rules version on the device. Null when
 * nothing is laid, the dictionary hasn't arrived, or this build doesn't have the game's rules version.
 */
export function previewMove(
  game: Pick<BoardGameView, 'rulesVersion' | 'shape' | 'size' | 'diagonals' | 'start' | 'bonuses'>,
  locked: TileData[],
  pending: TileData[],
  isWord: ((word: string) => boolean) | undefined,
): BoardGamePreview | null {
  const rules = findRules(game.rulesVersion);
  if (pending.length === 0 || !isWord || !rules || !game.start) return null;

  const move = rules.validateMove({
    shape: game.shape,
    size: game.size,
    locked,
    placed: pending,
    isWord: (word) => isWord(word),
    diagonals: game.diagonals,
    start: game.start,
  });
  if (!move.valid) {
    const words = move.words.filter(({ valid }) => !valid).map(({ word }) => word);
    return { valid: false, message: refusalMessage({ reason: move.reason ?? 'no-word-formed', words }) };
  }

  const premiums = game.bonuses.flatMap((cell) =>
    PREMIUM_KINDS.has(cell.kind) ? [{ row: cell.row, col: cell.col, kind: cell.kind as PremiumKind }] : [],
  );
  const scored = rules.scoreWords(move.words, premiums, [...locked, ...pending], new Set(pending.map(({ id }) => id)));
  const points =
    scored.reduce((total, word) => total + word.points, 0) + rules.bingoFor(pending.map(({ letter }) => letter));
  const words = scored.map(({ word }) => word);
  return { valid: true, words, points, message: `Would score ${pointsLabel(points)}: ${words.join(', ')}` };
}
